
import { getPositionWithFallback, useLocationPermissionSync } from "../../../../utils/getPositionWithFallback";
import React, { useState, useEffect, useCallback } from "react";
import {
  Drawer,
  Box,
  Typography,
  TextField,
  CircularProgress,
  Button,
  IconButton,
} from "@mui/material";
import { GoogleMap, Marker, Circle, InfoWindow } from "@react-google-maps/api";
import CustomButton from "../../../../components/CustomButton";
import CustomText, { textStyles } from "../../../../components/CustomText";
import { BASE_URL } from "../../../../components/BaseUrl";
import { useSelector } from "react-redux";
import { decryptToken } from "../../../../Utlis/Encryption";
import { toast } from "react-hot-toast";
import { useTranslation } from "react-i18next";
import SearchIcon from "@mui/icons-material/Search";
import profilecross from "../../../../assets/profilecross.svg";
import MultiSelectDropdown from "../../../../components/MultiSelectionDropdown"; // ✅ Ensure correct import
import LocationPermissionModal from "../../../../components/PermissionModal";
import {
  convertLocalDateTimeToUTC,
  convertUTCToLocalDateTime,
} from "../../../../components/dateTimeUtils";
const mapContainerStyle = { width: "100%", height: "100%" };
const markerIconUrl =
  "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-blue.png";

const fullDays = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const AddScheduleDrawer = ({ open, onClose, onAddSuccess, departmentId }) => {
  const token = useSelector((state) => state.auth.token);
  const decryptedToken = token ? decryptToken(token) : null;
  const { t } = useTranslation();

  const initialForm = {
  startTime: "",
  endTime: "",
  graceMinutes: "",
  breakMinutes: "",
  workingDays: [],
  locationName: "",
  radiusMeters: 100,
};


  const [form, setForm] = useState({
    startTime: "",
    endTime: "",
    graceMinutes: "",
    breakMinutes: "",
    workingDays: [],
    locationName: "",
    radiusMeters: 100,
  });

  const [workers, setWorkers] = useState([]);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetchingSettings, setFetchingSettings] = useState(false);
  const [hasExistingSettings, setHasExistingSettings] = useState(false);
  const [isMapPopupOpen, setIsMapPopupOpen] = useState(false);

  const [locationModalOpen, setLocationModalOpen] = useState(false);
const { company } = useSelector((state) => state.auth);
const companyId = company?.id;
console.log(companyId)
  // ✅ Fetch workers
  
  // reverse geocode function - defined early so it can be used in useEffect
  const fetchAddress = useCallback(([lat, lng]) => {
    if (!window.google?.maps) return;

    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ location: { lat, lng } }, (results, status) => {
      if (status === "OK" && results?.[0]) {
        setAddress(results[0].formatted_address || "Address not found");
      } else {
        setAddress("Address not found");
      }
    });
  }, []);
  
  // ✅ Fetch existing attendance settings
  useEffect(() => {
    const fetchExistingSettings = async () => {
      if (!open || !departmentId || !decryptedToken) return;

      setFetchingSettings(true);
      try {
        const res = await fetch(
          `${BASE_URL}/company-admins/departments/${departmentId}/attendance-settings`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${decryptedToken}`,
            },
          }
        );

        const json = await res.json();
        
        if (res.ok && !json.error && json.data?.hasSettings) {
          const { schedule, location } = json.data;
          
          setHasExistingSettings(true);
          
          // Populate form with existing schedule data
          if (schedule) {
            setForm((prev) => ({
              ...prev,
              workingDays: schedule.days || [],
              startTime: convertUTCToLocalInputTime(schedule.start),
              endTime: convertUTCToLocalInputTime(schedule.end),
              graceMinutes: schedule.graceMinutes?.toString() || "",
              breakMinutes: schedule.breakPolicy?.unpaid?.toString() || "",
            }));
          }
          
          // Populate location data
          if (location) {
            setForm((prev) => ({
              ...prev,
              locationName: location.locationName || "",
              radiusMeters: location.radiusMeters || 100,
            }));
            
            if (location.lat && location.lng) {
              const coords = [location.lat, location.lng];
              setCurrentLocation(coords);
              fetchAddress(coords);
            }
          }
        } else {
          // No existing settings, reset to initial form
          setHasExistingSettings(false);
          setForm(initialForm);
          setCurrentLocation(null);
          setAddress("Fetching address...");
          
          // Try to get current location if no existing location
          if (navigator.geolocation) {
            getPositionWithFallback(
              (pos) => {
                setCurrentLocation([pos.coords.latitude, pos.coords.longitude]);
              },
              () => {
                setLocationModalOpen(true);
              }
            );
          } else {
            setLocationModalOpen(true);
          }
        }
      } catch (err) {
        console.error("❌ Error fetching existing settings:", err);
        // On error, reset to initial form
        setHasExistingSettings(false);
        setForm(initialForm);
        setCurrentLocation(null);
        setAddress("Fetching address...");
        
        // Try to get current location
        if (navigator.geolocation) {
          getPositionWithFallback(
            (pos) => {
              setCurrentLocation([pos.coords.latitude, pos.coords.longitude]);
            },
            () => {
              setLocationModalOpen(true);
            }
          );
        } else {
          setLocationModalOpen(true);
        }
      } finally {
        setFetchingSettings(false);
      }
    };

    if (open) {
      fetchExistingSettings();
    }
  }, [open, departmentId, decryptedToken, fetchAddress]);

  useEffect(() => {
    if (open && !hasExistingSettings && !fetchingSettings) {
      // Only try to get location if no existing settings were loaded
      // Try to get location
      if (navigator.geolocation) {
        getPositionWithFallback(
          (pos) => {
            setCurrentLocation([pos.coords.latitude, pos.coords.longitude]);
          },
          () => {
            setLocationModalOpen(true); // 👈 show modal if blocked
          }
        );
      } else {
        setLocationModalOpen(true);
      }
    }
    
    // Reset state when drawer closes
    if (!open) {
      setHasExistingSettings(false);
      setFetchingSettings(false);
    }
  }, [open, hasExistingSettings, fetchingSettings]);

  // Get current location
  useEffect(() => {
    if (!currentLocation && navigator.geolocation) {
      getPositionWithFallback(
        (pos) => {
          setCurrentLocation([pos.coords.latitude, pos.coords.longitude]);
        },
        (err) => console.warn("Geolocation error:", err)
      );
    }
  }, [currentLocation]);

  // Reflect browser location allow/block changes live (no refresh needed).
  useLocationPermissionSync({
    enabled: open,
    onGranted: () => {
      setLocationModalOpen(false);
      if (!currentLocation) {
        getPositionWithFallback(
          (pos) => setCurrentLocation([pos.coords.latitude, pos.coords.longitude]),
          (err) => console.warn("Geolocation error:", err)
        );
      }
    },
    onDenied: () => setLocationModalOpen(true),
  });

  const toggleDay = (day) => {
    setForm((prev) => {
      const updated = prev.workingDays.includes(day)
        ? prev.workingDays.filter((d) => d !== day)
        : [...prev.workingDays, day];
      return { ...prev, workingDays: updated };
    });
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const convertUTCToLocalInputTime = (utcTimeValue) => {
    if (!utcTimeValue || utcTimeValue === "-") return "";

    const localTime = convertUTCToLocalDateTime(utcTimeValue, {
      locale: "en-GB",
      formatOptions: { hour: "2-digit", minute: "2-digit", hour12: false },
    });

    if (!localTime || localTime === "—") return "";
    return localTime.trim().slice(0, 5);
  };

  const convertLocalTimeToUTCTimeOnly = (timeStr) => {
    if (!timeStr) return "";

    const utcISO = convertLocalDateTimeToUTC(timeStr);
    if (!utcISO) return "";

    return new Date(utcISO).toISOString().slice(11, 16);
  };

  // ✅ Submit handler
  const handleSubmit = async () => {
    if (!departmentId) {
      toast.error("⚠️ Department ID is required");
      return;
    }

    // ⏰ Validate times
    if (form.startTime && form.endTime && form.endTime <= form.startTime) {
      toast.error("⚠️ Shift end time must be after shift start time");
      return;
    }

    // 📍 Validate location
    if (!currentLocation) {
      setLocationModalOpen(true);
      return;
    }

    const payload = {
      schedule: {
        days: form.workingDays,
        start: convertLocalTimeToUTCTimeOnly(form.startTime),
        end: convertLocalTimeToUTCTimeOnly(form.endTime),
        breakPolicy: { unpaid: Number(form.breakMinutes) || 0 },
        graceMinutes: Number(form.graceMinutes) || 0,
      },
      location: {
        lat: currentLocation ? currentLocation[0] : null,
        lng: currentLocation ? currentLocation[1] : null,
        radiusMeters: Number(form.radiusMeters) || 100,
        locationName: form.locationName,
      },
    };
console.log("➡️ Department Schedule Payload:", payload);
    setLoading(true);
    try {
      const res = await fetch(
        `${BASE_URL}/company-admins/departments/${departmentId}/attendance-settings`, // 👈 Department ID in URL
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(decryptedToken ? { Authorization: `Bearer ${decryptedToken}` } : {}),
          },
          body: JSON.stringify(payload),
        }
      );

      if (!res.ok) {
        const text = await res.text();
        toast.error(`❌ API Failed: ${res.status} - ${text}`);
        throw new Error(`Failed: ${res.status}`);
      }

      toast.success(hasExistingSettings ? "✅ Schedule Updated!" : "✅ Schedule Created!");
      onAddSuccess();
      onClose();
    } catch (err) {
      console.error("❌ Add Error", err);
      toast.error("❌ Add Error: " + err.message);
    } finally {
      setLoading(false);
    }
  };
  const [searchOpen, setSearchOpen] = useState(false);
const [searchQuery, setSearchQuery] = useState("");
const [address, setAddress] = useState("");
useEffect(() => {
  if (currentLocation) {
    fetchAddress(currentLocation);
  }
}, [currentLocation]);
// search
const handleSearch = async () => {
  if (!searchQuery.trim()) return toast.error("Enter a location");
  if (!window.google?.maps) {
    toast.error("Map is not ready");
    return;
  }

  const geocoder = new window.google.maps.Geocoder();
  geocoder.geocode({ address: searchQuery }, (results, status) => {
    if (status === "OK" && results?.[0]) {
      const location = results[0].geometry.location;
      const coords = [location.lat(), location.lng()];
      setCurrentLocation(coords);
      fetchAddress(coords);
    } else {
      toast.error("Location not found");
    }
  });
};
const defaultUSA = [37.0902, -95.7129]; // Center of USA

const getMarkerIcon = () => {
  if (!window.google?.maps) {
    return { url: markerIconUrl };
  }

  return {
    url: markerIconUrl,
    scaledSize: new window.google.maps.Size(25, 41),
    anchor: new window.google.maps.Point(12, 41),
  };
};

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: 560 } }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2,
          py: 1.5,
          borderBottom: "1px solid #E0E0E0",
          backgroundColor: "#f5f6f7",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <IconButton onClick={onClose}>
            <img src={profilecross} alt="close" />
          </IconButton>
          <CustomText
            sx={{
              ...textStyles.h1,
              fontSize: 18,
              fontWeight: 600,
              color: "#172B4D",
            }}
          >
            {hasExistingSettings ? t("editModal.title", "Edit Schedule") : t("editModal.titleAdd")}
          </CustomText>
        </Box>
      </Box>

      <Box sx={{ p: 3 }}>
        {fetchingSettings && (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 3 }}>
            <CircularProgress size={24} />
          </Box>
        )}
        {/* Days Row */}
        <Box sx={{ display: "flex", justifyContent: "center", mb: 3, gap: 1 }}>
          {fullDays.map((day) => {
            const short = day.slice(0, 3);
            const isSelected = form.workingDays.includes(short);
            return (
              <Box
                key={day}
                onClick={() => toggleDay(short)}
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: 600,
                  userSelect: "none",
                  bgcolor: isSelected ? "#1976d2" : "#e0e0e0",
                  color: isSelected ? "#fff" : "#000",
                  transition: "all 0.2s",
                }}
              >
                {day[0]}
              </Box>
            );
          })}
        </Box>

          <Box sx={{ mb: 3 }}>
  {/* Search toggle */}
  <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
    <IconButton
      onClick={() => setSearchOpen((p) => !p)}
      sx={{
        backgroundColor: "#1976d2",
        color: "#fff",
        width: 36,
        height: 36,
        "&:hover": { backgroundColor: "#115293" },
      }}
    >
      <SearchIcon fontSize="small" />
    </IconButton>
  </Box>

  {/* Search input */}
  {searchOpen && (
    <Box sx={{ mb: 2 }}>
      <input
        type="text"
        placeholder={t("searchLocationPlaceholder")}
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        onKeyDown={(e) => e.key === "Enter" && handleSearch()}
        style={{
          width: "100%",
          padding: "10px 14px",
          fontSize: "15px",
          border: "1px solid #ccc",
          borderRadius: "8px",
          fontFamily: "Poppins, sans-serif",
        }}
      />
    </Box>
  )}

  {/* Map */}
  {currentLocation && (
    <Box sx={{ height: 250, borderRadius: "8px", overflow: "hidden" }}>
      <GoogleMap
        mapContainerStyle={mapContainerStyle}
        center={{
          lat: (currentLocation || defaultUSA)[0],
          lng: (currentLocation || defaultUSA)[1],
        }}
        zoom={currentLocation ? 15 : 4}
        options={{
          fullscreenControl: false,
          mapTypeControl: false,
          streetViewControl: false,
        }}
      >
        <Marker
          position={{
            lat: currentLocation[0],
            lng: currentLocation[1],
          }}
          draggable
          icon={getMarkerIcon()}
          onClick={() => setIsMapPopupOpen(true)}
          onDragEnd={(e) => {
            const coords = [e.latLng.lat(), e.latLng.lng()];
            setCurrentLocation(coords);
            fetchAddress(coords);
          }}
        />
        {isMapPopupOpen && (
          <InfoWindow
            position={{
              lat: currentLocation[0],
              lng: currentLocation[1],
            }}
            onCloseClick={() => setIsMapPopupOpen(false)}
          >
            <Box sx={{ fontFamily: "Poppins, sans-serif" }}>
              {form.locationName || "Work Location"}
            </Box>
          </InfoWindow>
        )}
        <Circle
          center={{
            lat: currentLocation[0],
            lng: currentLocation[1],
          }}
          radius={Number(form.radiusMeters) || 1000}
          options={{
            strokeColor: "blue",
            strokeOpacity: 1,
            strokeWeight: 1,
            fillColor: "blue",
            fillOpacity: 0.1,
          }}
        />
      </GoogleMap>
    </Box>
  )}


        <textarea
  
           type="text"
    readOnly
    value={address}
        style={{
             width: "100%",
    padding: "12px",
    fontSize: "16px",
    border: "1px solid #ccc",
    borderRadius: "8px",
    marginTop: "10px",
    backgroundColor: "#f4f4f4",
    fontFamily: "Poppins, sans-serif",
    resize: "vertical",      // allow manual resize if needed
    overflowY: "auto",       // show scroll when text exceeds height
    lineHeight: "1.4",
        }}
      />
</Box>
        {/* )} */}

        {/* Location Name */}
        <Box sx={{ mb: 2 }}>
          <Typography sx={{ fontSize: "15px", mb: 0.5, fontFamily: "Poppins, sans-serif" }}>
              {t("editModal.labels.locationName")}
          </Typography>
          <TextField
            type="text"
            name="locationName"
            value={form.locationName}
            onChange={handleChange}
            placeholder={t("locationName")}
            size="small"
            fullWidth
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
                height: "50px",
                "& fieldset": { border: "2px solid #091E4224" },
                "&.Mui-focused fieldset": { border: "2px solid #006EC2" },
                "& input": {
                  fontSize: "13px",
                  fontFamily: "Poppins, sans-serif",
                },
              },
            }}
          />
        </Box>

        {/* Radius */}
        <Box sx={{ mb: 2 }}>
          <Typography sx={{ fontSize: "15px", mb: 0.5, fontFamily: "Poppins, sans-serif" }}>
          {t("editModal.labels.locationRadius")}
          </Typography>
          <TextField
            type="number"
            name="radiusMeters"
            value={form.radiusMeters}
            onChange={handleChange}
            size="small"
            fullWidth
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
                height: "50px",
                "& fieldset": { border: "2px solid #091E4224" },
                "&.Mui-focused fieldset": { border: "2px solid #006EC2" },
                "& input": {
                  fontSize: "13px",
                  fontFamily: "Poppins, sans-serif",
                },
              },
            }}
          />
        </Box>

        {/* Shift Times */}
        <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontSize: "15px", mb: 0.5, fontFamily: "Poppins, sans-serif" }}>
               {t("editModal.labels.shiftStart")}
            </Typography>
            <TextField
              type="time"
              name="startTime"
              value={form.startTime}
              onChange={handleChange}
              size="small"
              fullWidth
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  height: "50px",
                  "& fieldset": { border: "2px solid #091E4224" },
                  "&.Mui-focused fieldset": { border: "2px solid #006EC2" },
                  "& input": {
                    fontSize: "13px",
                    fontFamily: "Poppins, sans-serif",
                  },
                },
              }}
            />
          </Box>

          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontSize: "15px", mb: 0.5, fontFamily: "Poppins, sans-serif" }}>
             {t("editModal.labels.shiftEnd")}
            </Typography>
            <TextField
              type="time"
              name="endTime"
              value={form.endTime}
              onChange={handleChange}
              size="small"
              fullWidth
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  height: "50px",
                  "& fieldset": { border: "2px solid #091E4224" },
                  "&.Mui-focused fieldset": { border: "2px solid #006EC2" },
                  "& input": {
                    fontSize: "13px",
                    fontFamily: "Poppins, sans-serif",
                  },
                },
              }}
            />
          </Box>
        </Box>

        {/* Grace + Break */}
        <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontSize: "14px", mb: 0.5, fontFamily: "Poppins, sans-serif" }}>
          {t("editModal.labels.graceMinutes")}
            </Typography>
            <TextField
              type="number"
              name="graceMinutes"
              value={form.graceMinutes}
              onChange={handleChange}
              size="small"
              fullWidth
              inputProps={{ 
                min: 0,
                onKeyDown: (e) => {
                  if (e.key === '-' || e.key === 'e' || e.key === 'E') {
                    e.preventDefault();
                  }
                }
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  height: "50px",
                  "& fieldset": { border: "2px solid #091E4224" },
                  "&.Mui-focused fieldset": { border: "2px solid #006EC2" },
                  "& input": {
                    fontSize: "13px",
                    fontFamily: "Poppins, sans-serif",
                  },
                },
              }}
            />
          </Box>

          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontSize: "14px", mb: 0.5, fontFamily: "Poppins, sans-serif" }}>
                  {t("editModal.labels.breakMinutes")}
            </Typography>
            <TextField
              type="number"
              name="breakMinutes"
              value={form.breakMinutes}
              onChange={handleChange}
              size="small"
              fullWidth
              inputProps={{ 
                min: 0,
                onKeyDown: (e) => {
                  if (e.key === '-' || e.key === 'e' || e.key === 'E') {
                    e.preventDefault();
                  }
                }
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  height: "50px",
                  "& fieldset": { border: "2px solid #091E4224" },
                  "&.Mui-focused fieldset": { border: "2px solid #006EC2" },
                  "& input": {
                    fontSize: "13px",
                    fontFamily: "Poppins, sans-serif",
                  },
                },
              }}
            />
          </Box>
        </Box>

        {/* Actions */}
        <Box sx={{ display: "flex", justifyContent: "flex-start", gap: 2, mt: 2 }}>
          <CustomButton
            size="small"
            onClick={handleSubmit}
            disabled={loading}
            sx={{ px: 2, display: "flex", alignItems: "center" }}
            label={
              loading ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <CircularProgress size={18} sx={{ color: "white" }} />
                  {hasExistingSettings ? t("editModal.buttons.update", "Update") : t("editModal.create", "Create")}
                </Box>
              ) : (
                hasExistingSettings ? t("editModal.buttons.update", "Update") : t("editModal.create", "Create")
              )
            }
          />
        </Box>
      </Box>
            <LocationPermissionModal open={locationModalOpen} onCancel={() => setLocationModalOpen(false)} />

    </Drawer>
  );
};

export default AddScheduleDrawer;

